"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge } from "@components/base/badges/badges";
import { Button } from "@components/base/buttons/button";
import { Checkbox } from "@components/base/checkbox/checkbox";
import { Input } from "@components/base/input/input";
import { RadioButtonBase } from "@components/base/radio-buttons/radio-buttons";
import { Tooltip, TooltipTrigger } from "@components/base/tooltip/tooltip";
import FAQSection from "@components/foundations/faq-section/FAQSection";
import { PLANS_FAQ_DESCRIPTION, PLANS_FAQS } from "@consts/faqs";
import { PLANS_COMPARISON_ROWS, PLANS_PRICING_COPY } from "@consts/plans";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Lock01, XCircle } from "@untitledui/icons";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Radio, RadioGroup } from "react-aria-components";
import { StarIcon } from "@/components/foundations/rating-stars";
import { MASTHEAD_TITLE_CLASS } from "@/consts/typography";
import { type PublicProOffer, usePublicProOffers } from "@/hooks/use-public-pro-offers";
import { useSession } from "@/lib/auth-client";
import { consumeOneTimeToken } from "@/lib/auth/ios-handoff";
import { beginProCheckout } from "@/lib/billing/begin-pro-checkout";
import { getCheckoutErrorSummaryItems, getCheckoutFieldErrors, type CheckoutFieldErrors } from "@/lib/billing/checkout-form-validation";
import type { SubscriptionBillingSnapshot } from "@/lib/billing/subscription-snapshot";
import { isOfferTrialEligibleForUser, isReturningBillingCustomer } from "@/lib/billing/trial-eligibility";
import { effectiveTierPlanId } from "@/lib/membership-plan";
import { capturePosthogEvent } from "@/lib/posthog-browser";
import { hasProductFeatureBypassClient } from "@/lib/product-feature-bypass-client";
import { isProdEmployeeDiscountEmail } from "@/lib/test-users/classify-email";
import { useAuth } from "@/providers/auth";
import { cx } from "@/utils/cx";
function PlansCheckoutValidationSummary(t0) {
  const $ = _c(19);
  const {
    errors,
    visible
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== errors || $[1] !== visible) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = getCheckoutErrorSummaryItems(errors);
      if (!visible || items.length === 0) {
        t7 = null;
        break bb0;
      }
      t3 = "alert";
      t4 = "polite";
      t5 = "w-full rounded-lg border border-error_subtle bg-utility-error-50 p-4 text-left";
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <p className="text-sm font-semibold text-secondary">{PLANS_PRICING_COPY.checkoutErrorsSummaryTitle}</p>;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t1 = "mt-2 list-disc space-y-1.5 pl-5 text-sm text-fg-error-secondary";
      t2 = items.map(_temp);
    }
    $[0] = errors;
    $[1] = visible;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t2) {
    t8 = <ul className={t1}>{t2}</ul>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t8) {
    t9 = <div role={t3} aria-live={t4} className={t5}>{t6}{t8}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp(t0) {
  const {
    key,
    message
  } = t0;
  return <li key={key}>{message}</li>;
}
function formatMoneyCentsWhole(unitAmount: number, currency: string) {
  const amount = unitAmount / 100;
  const isWhole = unitAmount % 100 === 0;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2
  }).format(amount);
}
function PlanOptionRadioIndicator(t0) {
  const $ = _c(4);
  const {
    isSelected,
    isDisabled,
    isFocusVisible
  } = t0;
  let t1;
  if ($[0] !== isDisabled || $[1] !== isFocusVisible || $[2] !== isSelected) {
    t1 = <RadioButtonBase size="md" isSelected={isSelected} isDisabled={isDisabled} isFocusVisible={isFocusVisible} className="size-6! min-h-6! min-w-6! shrink-0" />;
    $[0] = isDisabled;
    $[1] = isFocusVisible;
    $[2] = isSelected;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function PlansCheckoutBestValueBadge() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-secondary_alt bg-primary px-2.5 py-1 text-xs font-medium text-secondary shadow-sm"><span className="size-1.5 shrink-0 rounded-md bg-fg-success-secondary" aria-hidden={true} />Best Value</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PlansCheckoutOfferCardBody(t0) {
  const $ = _c(29);
  const {
    offer,
    isSelected: t1,
    showTrialPricing
  } = t0;
  const isSelected = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== offer.currency || $[1] !== offer.displayListAmountMajor) {
    t2 = offer.displayListAmountMajor != null ? formatMoneyCentsWhole(Math.round(offer.displayListAmountMajor * 100), offer.currency) : null;
    $[0] = offer.currency;
    $[1] = offer.displayListAmountMajor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const listPrice = t2;
  let t3;
  if ($[3] !== offer.currency || $[4] !== offer.unitAmount || $[5] !== showTrialPricing) {
    t3 = showTrialPricing ? "$0" : formatMoneyCentsWhole(offer.unitAmount, offer.currency);
    $[3] = offer.currency;
    $[4] = offer.unitAmount;
    $[5] = showTrialPricing;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const todayPrice = t3;
  let t4;
  if ($[7] !== offer.isBestValue) {
    t4 = offer.isBestValue ? <PlansCheckoutBestValueBadge /> : null;
    $[7] = offer.isBestValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <div className="flex shrink-0 items-center">{t4}</div>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isSelected || $[12] !== listPrice || $[13] !== showTrialPricing) {
    t6 = listPrice && !showTrialPricing ? <span className={cx("font-display text-display-sm font-semibold line-through", isSelected ? "text-brand-300/75 dark:text-brand-200/80" : "text-fg-disabled_subtle")}>{listPrice}</span> : null;
    $[11] = isSelected;
    $[12] = listPrice;
    $[13] = showTrialPricing;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== todayPrice) {
    t7 = <span className="font-display text-display-sm font-semibold text-secondary">{todayPrice}</span>;
    $[15] = todayPrice;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="shrink-0 text-sm font-medium text-text-tertiary">Today</span>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t7) {
    t9 = <div className="flex flex-nowrap items-baseline justify-end gap-2">{t6}{t7}{t8}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== offer.cardDescription) {
    t10 = offer.cardDescription ? <p className="text-sm leading-5 whitespace-nowrap text-text-tertiary">{offer.cardDescription}</p> : null;
    $[21] = offer.cardDescription;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="flex min-w-0 flex-col items-end gap-1 text-right">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t5) {
    t12 = <div className="flex items-start justify-between gap-3 p-4">{t5}{t11}</div>;
    $[26] = t11;
    $[27] = t5;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
const PLAN_COMPARISON_ROW_COUNT = PLANS_COMPARISON_ROWS.length;
type PlanFeatureTone = "free-included" | "free-locked" | "pro";
function PlanFeatureLabelWithTooltip(t0) {
  const $ = _c(10);
  const {
    label,
    detail,
    tone
  } = t0;
  const textClass = tone === "free-locked" ? "text-text-tertiary/30" : "text-text-tertiary underline underline-offset-6 decoration-fg-brand-primary decoration-dotted [text-decoration-skip-ink:none]";
  const t1 = tone !== "free-locked" && "hover:text-text-primary";
  let t2;
  if ($[0] !== t1 || $[1] !== textClass) {
    t2 = cx("cursor-pointer rounded-sm text-left text-md leading-6 wrap-break-word outline-hidden transition-colors", "focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary", t1, textClass);
    $[0] = t1;
    $[1] = textClass;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label || $[4] !== t2) {
    t3 = <TooltipTrigger className={t2}>{label}</TooltipTrigger>;
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== detail || $[7] !== label || $[8] !== t3) {
    t4 = <Tooltip title={label} description={detail} placement="top" delay={200}>{t3}</Tooltip>;
    $[6] = detail;
    $[7] = label;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function PlansComparisonProPricingBlock(t0) {
  const $ = _c(20);
  const {
    offersLoading,
    authLoading,
    hasSellableOffers,
    selectedOffer,
    showTrialPricing
  } = t0;
  if (offersLoading || authLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-text-tertiary">Loading plan options…</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!hasSellableOffers || !selectedOffer) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-text-tertiary">Prices unavailable.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== selectedOffer.currency || $[3] !== selectedOffer.displayListAmountMajor || $[4] !== selectedOffer.recurringInterval) {
    t1 = selectedOffer.displayListAmountMajor != null && <div className="flex shrink-0 flex-col text-fg-disabled_subtle"><span className="plans-compare-pro-pricing__amount line-through">{formatMoneyCentsWhole(Math.round(selectedOffer.displayListAmountMajor * 100), selectedOffer.currency)}</span><span className="plans-compare-pro-pricing__suffix font-semibold">/ {selectedOffer.recurringInterval === "year" ? "year" : "month"}</span></div>;
    $[2] = selectedOffer.currency;
    $[3] = selectedOffer.displayListAmountMajor;
    $[4] = selectedOffer.recurringInterval;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== selectedOffer.currency || $[7] !== selectedOffer.unitAmount || $[8] !== showTrialPricing) {
    t2 = showTrialPricing ? "Free" : formatMoneyCentsWhole(selectedOffer.unitAmount, selectedOffer.currency);
    $[6] = selectedOffer.currency;
    $[7] = selectedOffer.unitAmount;
    $[8] = showTrialPricing;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <span className="plans-compare-pro-pricing__amount font-semibold">{t2}</span>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = showTrialPricing ? "For 7 Days" : `Today${selectedOffer.recurringInterval === "year" ? " (prepaid year)" : ""}`;
  let t5;
  if ($[12] !== t4) {
    t5 = <span className="plans-compare-pro-pricing__suffix font-semibold">{t4}</span>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <div className="flex shrink-0 flex-col text-text-primary">{t3}{t5}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t6) {
    t7 = <div className="plans-compare-pro-pricing flex min-w-0 flex-nowrap items-end gap-3 font-semibold sm:gap-4">{t1}{t6}</div>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function PlansComparisonFreePlanTitle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><div className="flex items-center gap-2.5"><img src="/assets/logos/logo-tier-1.svg" alt="" className="h-8 w-[28.76px] shrink-0" width={29} height={32} /><h2 className="text-display-xs font-semibold text-text-primary">Unilytics</h2></div><p className="text-display-xl font-semibold tracking-[-0.02em] text-text-primary">Free</p></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PlansComparisonProPlanTitle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-2.5"><img src="/assets/logos/logo-tier-2.svg" alt="" className="h-8 w-[28.76px] shrink-0" width={29} height={32} /><h2 className="text-display-xs font-semibold text-text-primary">Unilytics Pro</h2></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PlansComparisonFreeFeatureIcon(t0) {
  const $ = _c(2);
  const {
    included
  } = t0;
  let t1;
  if ($[0] !== included) {
    t1 = included ? <CheckCircle className="size-6 shrink-0 text-fg-brand-primary" /> : <XCircle className="size-6 shrink-0 text-fg-quaternary" />;
    $[0] = included;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export default function PlansPageClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isIosClient = searchParams.get("client") === "ios";
  const ott = searchParams.get("ott")?.trim() ?? "";
  const {
    data: session,
    refetch: refetchSession
  } = useSession();
  const {
    user,
    isSessionResolving,
    isLoading: authLoading,
    refetchMembership
  } = useAuth();
  const hasTrackedPageView = useRef(false);
  const ottConsumed = useRef(false);

  // App → web handoff: verify one-time token so Safari has a cookie session.
  useEffect(() => {
    if (!ott || ottConsumed.current) return;
    ottConsumed.current = true;
    void (async () => {
      const result = await consumeOneTimeToken(ott);
      if (result.ok) {
        await refetchSession({
          query: {
            disableCookieCache: true
          }
        });
        await refetchMembership();
        // Drop ott from the URL so refresh doesn't re-consume.
        const next = new URLSearchParams(searchParams.toString());
        next.delete("ott");
        if (!next.has("client")) next.set("client", "ios");
        router.replace(`/plans?${next.toString()}`);
      }
    })();
  }, [ott, refetchMembership, refetchSession, router, searchParams]);
  const {
    data: offersPayload,
    isLoading: offersLoading,
    isError: offersError
  } = usePublicProOffers();
  const effectiveTier = user?.membership ? effectiveTierPlanId(user.membership.planId) : "free";
  const hasStaffProductBypass = hasProductFeatureBypassClient(user?.role, user?.permissions);
  const isFreeLoggedIn = Boolean(session?.user) && !isSessionResolving && !hasStaffProductBypass && effectiveTier === "free";
  const isProLoggedIn = Boolean(session?.user) && !isSessionResolving && !hasStaffProductBypass && effectiveTier === "pro";
  const isProWithStripe = isProLoggedIn && user?.membership?.hasStripeSubscription === true;
  const subscriptionQuery = useQuery({
    queryKey: ["billingSubscription", "plans", user?.id],
    queryFn: async (): Promise<{
      subscription: (SubscriptionBillingSnapshot & {
        currentPriceId?: string;
      }) | null;
    }> => {
      const res = await fetch("/api/billing/subscription", {
        credentials: "include"
      });
      if (!res.ok) {
        throw new Error("Could not load subscription.");
      }
      return res.json() as Promise<{
        subscription: (SubscriptionBillingSnapshot & {
          currentPriceId?: string;
        }) | null;
      }>;
    },
    enabled: Boolean(session?.user) && isProWithStripe,
    staleTime: 30_000
  });
  const [email, setEmail] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [trialAccepted, setTrialAccepted] = useState(false);
  const [yearlyAccepted, setYearlyAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [existingSubscription, setExistingSubscription] = useState(false);
  const [checkoutAttempted, setCheckoutAttempted] = useState(false);
  const yearlyConsentRef = useRef<HTMLDivElement>(null);
  const yearlyCheckboxInputRef = useRef<HTMLInputElement>(null);
  const [emailBlurred, setEmailBlurred] = useState(false);
  const offers = offersPayload?.offers ?? [];
  const hasSellableOffers = offersPayload?.hasSellableOffers === true;
  /** First sellable offer by admin sort order — same default pre-selected in checkout. */
  const defaultProOffer: PublicProOffer | null = offers[0] ?? null;
  const selectedOffer: PublicProOffer | null = offers.find(o => o.offerId === selectedOfferId) ?? defaultProOffer;
  useEffect(() => {
    if (hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    void capturePosthogEvent("viewed_plans_page");
  }, []);
  useEffect(() => {
    if (session?.user?.email) {
      setEmail(session.user.email);
    }
  }, [session?.user?.email]);
  useEffect(() => {
    if (defaultProOffer && !selectedOfferId) {
      setSelectedOfferId(defaultProOffer.offerId);
    }
  }, [defaultProOffer, selectedOfferId]);
  const isLoggedIn = Boolean(session?.user);
  const isReturningCustomer = isReturningBillingCustomer(user?.membership?.hasStripeCustomerId);
  const selectedOfferShowsTrial = selectedOffer != null && isOfferTrialEligibleForUser(selectedOffer, isReturningCustomer);
  const requiresTrialAcceptance = selectedOfferShowsTrial;
  const requiresReturningMonthlyAcceptance = isReturningCustomer && selectedOffer?.recurringInterval === "month";
  const requiresMonthlyPlanAcceptance = requiresTrialAcceptance || requiresReturningMonthlyAcceptance;
  const checkoutValidationErrors = useMemo(() => getCheckoutFieldErrors({
    email,
    isLoggedIn,
    selectedOffer,
    trialAccepted,
    yearlyAccepted,
    requiresTrialAcceptance,
    requiresMonthlyPlanAcceptance
  }), [email, isLoggedIn, selectedOffer, trialAccepted, yearlyAccepted, requiresTrialAcceptance, requiresMonthlyPlanAcceptance]);
  const isCheckoutFormValid = Object.keys(checkoutValidationErrors).length === 0;
  const showCheckoutErrors = checkoutAttempted ? checkoutValidationErrors : {};
  const showCheckoutErrorSummary = checkoutAttempted && !isCheckoutFormValid;
  const showEmailError = (checkoutAttempted || emailBlurred) && checkoutValidationErrors.email ? checkoutValidationErrors.email : undefined;
  const employeeDiscountStarVisible = !session?.user && isProdEmployeeDiscountEmail(email);
  const revealCheckoutValidation = () => {
    setCheckoutAttempted(true);
    // Only the yearly consent is missing → bring it into view and focus it.
    if (checkoutValidationErrors.yearly && !checkoutValidationErrors.email && !checkoutValidationErrors.trial) {
      yearlyConsentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      yearlyCheckboxInputRef.current?.focus({
        preventScroll: true
      });
    }
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailAlreadyExists(false);
    setExistingSubscription(false);
    if (!hasSellableOffers || !selectedOffer) {
      return;
    }
    if (!isCheckoutFormValid) {
      revealCheckoutValidation();
      return;
    }
    setSubmitting(true);
    let didRedirect = false;
    try {
      const result_0 = await beginProCheckout({
        draft: {
          offerId: selectedOffer.offerId,
          trialAccepted: requiresMonthlyPlanAcceptance ? trialAccepted : true,
          yearlyAccepted: selectedOffer.recurringInterval === "year" ? yearlyAccepted : true,
          source: "plans",
          ...(isIosClient ? {
            client: "ios" as const
          } : {})
        },
        ...(!session?.user ? {
          email
        } : {}),
        router
      });
      if (result_0.mode === "hosted" && "error" in result_0) {
        if (result_0.code === "EMAIL_ALREADY_EXISTS") {
          setEmailAlreadyExists(true);
          return;
        }
        if (result_0.code === "EXISTING_SUBSCRIPTION") {
          setExistingSubscription(true);
          return;
        }
        throw new Error(result_0.error);
      }
      if (result_0.mode === "hosted") {
        void capturePosthogEvent("checkout_session_started", {
          offerId: selectedOffer.offerId,
          source: "plans"
        });
      }
      didRedirect = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      if (!didRedirect) {
        setSubmitting(false);
      }
    }
  };
  const sub = subscriptionQuery.data?.subscription;
  const hideCheckoutSection = isProLoggedIn || hasStaffProductBypass;
  const showTrialBlock = requiresTrialAcceptance && !hideCheckoutSection;
  const showReturningMonthlyBlock = requiresReturningMonthlyAcceptance && !hideCheckoutSection;
  const showYearlyBlock = selectedOffer?.recurringInterval === "year" && !hideCheckoutSection;
  const showFreePlanBadge = !hasStaffProductBypass && effectiveTier === "free";
  const showProPlanBadge = !hasStaffProductBypass;
  const plansCompareStyle = {
    "--plans-feature-count": PLAN_COMPARISON_ROW_COUNT
  } as CSSProperties;
  const canPurchaseCheckout = Boolean(hasSellableOffers) && Boolean(selectedOffer) && !hasStaffProductBypass && !isProLoggedIn;

  /** Page hero / checkout form depends on state — comparison grid stays the same. */
  return <div className="pb-16">
			<div className="page-shell flex flex-col items-center pt-6 md:pt-10">
				<div className="mb-8 flex w-full max-w-5xl flex-col items-center gap-4 text-center md:mb-12 md:gap-6">
					<h1 className={MASTHEAD_TITLE_CLASS}>
						{hasStaffProductBypass ? "Plans & Pricing" : isProLoggedIn ? "Plans & Pricing" : isFreeLoggedIn ? "Upgrade to Unilytics Pro" : PLANS_PRICING_COPY.heroTitle}
					</h1>
					{hasStaffProductBypass && <p className="max-w-3xl text-lg leading-7 text-balance text-text-tertiary md:text-xl md:leading-[30px]">
							Staff accounts are not billed on a public plan. The comparison below is for reference.
						</p>}
				</div>

				<div className="plans-compare-shell w-full max-w-5xl">
					<div className="plans-compare-grid" style={plansCompareStyle}>
						<div className="plans-compare-slot plans-compare-slot--free px-0 pt-0 pb-4 max-md:flex max-md:flex-1 max-md:flex-col md:px-12 md:py-12">
							<article className="plans-compare-card plans-compare-card--free relative rounded-2xl border border-secondary_alt bg-primary shadow-lg">
								{showFreePlanBadge && <Badge color="gray" type="pill-color" size="xl" className="absolute -top-5 right-6 z-10 shadow-xs">
										Your Current Plan
									</Badge>}
								<div className="flex flex-col gap-6 p-8">
									<PlansComparisonFreePlanTitle />
								</div>
								<div className="plans-compare-divider mx-8 h-px bg-border-secondary" />
								<ul className="plans-compare-features">
									{PLANS_COMPARISON_ROWS.map(row => <li key={row.label} className="flex min-w-0 items-start gap-3 px-8 text-left md:pb-4 md:last:pb-8">
											<PlansComparisonFreeFeatureIcon included={row.free} />
											<PlanFeatureLabelWithTooltip label={row.label} detail={row.detail} tone={row.free ? "free-included" : "free-locked"} />
										</li>)}
								</ul>
							</article>
						</div>

						<div className={cx("plans-compare-slot plans-compare-slot--pro plans-pricing-pro-column-bg relative rounded-t-3xl px-4 py-6 pt-10 max-md:flex max-md:flex-1 max-md:flex-col md:rounded-t-3xl md:px-12 md:py-12", hideCheckoutSection ? "rounded-b-3xl md:rounded-b-3xl" : "md:rounded-b-none")}>
							<article className="plans-compare-card plans-compare-card--pro relative rounded-2xl border border-secondary_alt bg-primary shadow-sm ring-4 ring-focus-ring ring-offset-2 ring-offset-bg-primary">
								{showProPlanBadge && <Badge color="success" type="pill-color" size="xl" className="absolute -top-5 right-6 z-10 shadow-xs">
										{isProLoggedIn ? "Your Current Plan" : "Recommended"}
									</Badge>}
								<div className="flex flex-col gap-6 p-8">
									<PlansComparisonProPlanTitle />
									<PlansComparisonProPricingBlock offersLoading={offersLoading} authLoading={authLoading} hasSellableOffers={hasSellableOffers} selectedOffer={selectedOffer} showTrialPricing={selectedOfferShowsTrial} />
								</div>
								<div className="plans-compare-divider mx-8 h-px bg-border-secondary" />
								<ul className="plans-compare-features">
									{PLANS_COMPARISON_ROWS.map(row_0 => <li key={row_0.label} className="flex min-w-0 items-start gap-3 px-8 text-left md:pb-4 md:last:pb-8">
											<CheckCircle className="mt-0.5 size-6 shrink-0 text-fg-brand-primary" />
											<PlanFeatureLabelWithTooltip label={row_0.label} detail={row_0.detail} tone="pro" />
										</li>)}
								</ul>
							</article>
							{hasStaffProductBypass ? <p className="mx-auto max-w-md px-4 pt-2 pb-6 text-center text-lg leading-7 text-balance text-text-tertiary md:pb-8">
									Checkout and sign-up aren&apos;t available for staff accounts. Your access
									isn&apos;t tied to a public subscription—the plan comparison above is for
									reference only.
								</p> : null}
						</div>
					</div>
				</div>

				{!hideCheckoutSection && <div className={cx("plans-pricing-checkout-bg flex w-full max-w-5xl flex-col items-center rounded-b-2xl px-4 py-8 md:mt-0 md:rounded-tl-3xl md:rounded-tr-none md:rounded-br-3xl md:px-12 md:pt-8 md:pb-12")}>
						<div className="flex w-full max-w-5xl flex-col items-center gap-6 md:gap-8">
							<div className="flex max-w-5xl flex-col items-center gap-2 text-center md:gap-3">
								<h2 className={MASTHEAD_TITLE_CLASS}>
									{isReturningCustomer ? PLANS_PRICING_COPY.reactivateHeadline : PLANS_PRICING_COPY.trialHeadline}
								</h2>
								<p className="max-w-3xl text-lg leading-6 text-secondary md:text-xl md:leading-7">
									{isReturningCustomer ? PLANS_PRICING_COPY.reactivateSub : PLANS_PRICING_COPY.trialSub}
								</p>
							</div>

							{offersError && <div className="w-full max-w-2xl rounded-lg border border-error_subtle bg-utility-error-50 p-4 text-center text-sm text-secondary">
									Could not load plans. Refresh the page or try again later.
								</div>}

							{!offersLoading && !hasSellableOffers && !offersError && <div className="w-full max-w-2xl rounded-2xl border border-secondary_alt bg-primary p-8 text-center shadow-lg" role="status">
									<p className="text-lg font-semibold text-text-primary">
										Subscriptions are not available to purchase right now
									</p>
									<p className="mt-2 text-text-tertiary">
										Please check back later or{" "}
										<Link href="/contact-us" className="font-medium text-brand-secondary hover:underline">
											contact support
										</Link>{" "}
										for help.
									</p>
								</div>}

							{hasSellableOffers && canPurchaseCheckout && <form noValidate onSubmit={onSubmit} className="w-full max-w-2xl rounded-2xl border border-secondary_alt bg-primary p-4 shadow-lg md:p-8">
									<p className="mb-6 flex items-center gap-2 text-sm text-text-tertiary">
										<Lock01 className="size-5 shrink-0 text-fg-quaternary" aria-hidden />
										Payment details are entered securely on our checkout page.
									</p>
									<div className="flex flex-col gap-6">
										{!session?.user && <div className="relative">
												<Input label="Email Address" type="email" inputMode="email" isRequired isInvalid={!!showEmailError || emailAlreadyExists} hint={showEmailError} value={email} onChange={v => {
                  setEmail(v);
                  setEmailAlreadyExists(false);
                  setExistingSubscription(false);
                }} onBlur={() => setEmailBlurred(true)} name="email" autoComplete="email" isDisabled={!!session?.user} />
												<span aria-hidden className={cx("pointer-events-none absolute top-9 right-3 text-yellow-500 transition duration-200 ease-out", employeeDiscountStarVisible ? "scale-100 opacity-100" : "scale-75 opacity-0")}>
													<StarIcon progress={100} className="size-5" />
												</span>
											</div>}

										{session?.user && isFreeLoggedIn && <p className="text-sm text-text-tertiary">
												Signed in as <span className="font-medium">{session.user.email}</span>
											</p>}

										<div className="border-t border-secondary pt-6">
											<p className="mb-3 text-sm font-semibold text-secondary">
												{PLANS_PRICING_COPY.planSelectLabel}
											</p>
											<RadioGroup value={selectedOfferId ?? undefined} onChange={v_0 => {
                  setSelectedOfferId(v_0);
                  setTrialAccepted(false);
                  setYearlyAccepted(false);
                  setCheckoutAttempted(false);
                }} className="flex flex-col gap-4" aria-label="Billing plan">
												{offers.map(o_0 => <Radio key={o_0.offerId} value={o_0.offerId} className={({
                    isSelected,
                    isFocusVisible
                  }) => cx("block w-full cursor-pointer rounded-xl border-2 transition-colors outline-none", isSelected ? "border-brand bg-brand-primary shadow-lg" : "border-secondary_alt bg-primary", isFocusVisible && "ring-2 ring-focus-ring ring-offset-2")}>
														{({
                      isSelected: isSelected_0,
                      isDisabled,
                      isFocusVisible: isFocusVisible_0
                    }) => <>
																<div className="flex items-center gap-2 border-b border-secondary px-4 py-3.5">
																	<img src="/assets/logos/logo-tier-2.svg" alt="" className="h-6 w-[22px] shrink-0" />
																	<span className="min-w-0 flex-1 text-left text-lg leading-snug font-semibold text-secondary">
																		{o_0.cardLabel}
																	</span>
																	<PlanOptionRadioIndicator isSelected={isSelected_0} isDisabled={isDisabled} isFocusVisible={isFocusVisible_0} />
																</div>
																<PlansCheckoutOfferCardBody offer={o_0} isSelected={isSelected_0} showTrialPricing={isOfferTrialEligibleForUser(o_0, isReturningCustomer)} />
															</>}
													</Radio>)}
											</RadioGroup>
										</div>

										{showTrialBlock && <div className={cx("rounded-lg p-4", showCheckoutErrors.trial ? "bg-utility-error-50 ring-1 ring-error_subtle" : "bg-secondary/50")}>
												<Checkbox isSelected={trialAccepted} isInvalid={!!showCheckoutErrors.trial} onChange={setTrialAccepted} size="md" label={<span className="font-semibold text-primary">
															{PLANS_PRICING_COPY.trialCheckbox}
														</span>} hint={<span className="text-secondary">
															{PLANS_PRICING_COPY.trialDisclosure}
														</span>} />
												{showCheckoutErrors.trial && <p className="mt-3 text-sm font-medium text-fg-error-secondary" role="alert">
														{showCheckoutErrors.trial}
													</p>}
											</div>}

										{showReturningMonthlyBlock && <div className={cx("rounded-lg p-4", showCheckoutErrors.trial ? "bg-utility-error-50 ring-1 ring-error_subtle" : "bg-secondary/50")}>
												<Checkbox isSelected={trialAccepted} isInvalid={!!showCheckoutErrors.trial} onChange={setTrialAccepted} size="md" label={<span className="font-semibold text-primary">
															{PLANS_PRICING_COPY.monthlyCheckboxNoTrial}
														</span>} hint={<span className="text-secondary">
															{PLANS_PRICING_COPY.monthlyDisclosureNoTrial}
														</span>} />
												{showCheckoutErrors.trial && <p className="mt-3 text-sm font-medium text-fg-error-secondary" role="alert">
														{showCheckoutErrors.trial}
													</p>}
											</div>}

										{showYearlyBlock && <div ref={yearlyConsentRef} className={cx("scroll-mt-24 rounded-lg p-4", showCheckoutErrors.yearly ? "bg-utility-error-50 ring-1 ring-error_subtle" : "bg-secondary/50")}>
												<Checkbox inputRef={yearlyCheckboxInputRef} isSelected={yearlyAccepted} isInvalid={!!showCheckoutErrors.yearly} onChange={setYearlyAccepted} size="md" label={<span className="font-semibold text-primary">
															{PLANS_PRICING_COPY.yearlyCheckbox}
														</span>} hint={<span className="text-secondary">
															{PLANS_PRICING_COPY.yearlyDisclosure}
														</span>} />
												{showCheckoutErrors.yearly && <p className="mt-3 text-sm font-medium text-fg-error-secondary" role="alert">
														{showCheckoutErrors.yearly}
													</p>}
											</div>}

										{emailAlreadyExists && <div className="rounded-lg border border-error_subtle bg-utility-error-50 p-4 text-left" role="alert">
												<p className="text-sm font-semibold text-secondary">
													This email is already registered
												</p>
												<p className="mt-2 text-sm text-text-tertiary">
													Log in to continue to checkout, or use forgot password if you don&apos;t
													remember yours. If you believe this is a mistake, contact support.
												</p>
												<ul className="mt-3 flex flex-col gap-2 text-sm font-semibold text-brand-secondary">
													<li>
														<Link href="/sign-in" className="hover:underline">
															Log In
														</Link>
													</li>
													<li>
														<Link href="/forgot-password" className="hover:underline">
															Forgot password
														</Link>
													</li>
													<li>
														<Link href="/contact-us" className="hover:underline">
															Contact support
														</Link>
													</li>
												</ul>
											</div>}

										{existingSubscription && <div className="rounded-lg border border-error_subtle bg-utility-error-50 p-4 text-left" role="alert">
												<p className="text-sm font-semibold text-secondary">
													You already have an active subscription
												</p>
												<p className="mt-2 text-sm text-text-tertiary">
													Sign in to manage billing, or open the welcome page from your last
													checkout if you just paid. Contact support if you need help.
												</p>
												<ul className="mt-3 flex flex-col gap-2 text-sm font-semibold text-brand-secondary">
													<li>
														<Link href="/sign-in" className="hover:underline">
															Log In
														</Link>
													</li>
													<li>
														<Link href="/account#membership" className="hover:underline">
															My Account
														</Link>
													</li>
													<li>
														<Link href="/contact-us" className="hover:underline">
															Contact support
														</Link>
													</li>
												</ul>
											</div>}

										{error && <p className="text-sm font-medium text-fg-error-secondary" role="alert">
												{error}
											</p>}

										<div className="flex w-full flex-col items-stretch gap-3 sm:items-center">
											<PlansCheckoutValidationSummary errors={checkoutValidationErrors} visible={showCheckoutErrorSummary} />
											<div className="relative w-full sm:w-auto sm:self-center">
												<Button type="submit" color="primary" size="2xl" className="w-full" isDisabled={submitting || !isCheckoutFormValid} isLoading={submitting}>
													{PLANS_PRICING_COPY.submitCta}
												</Button>
												{!submitting && !isCheckoutFormValid ? <button type="button" className="absolute inset-0 z-10 cursor-not-allowed rounded-lg opacity-0" aria-label={PLANS_PRICING_COPY.checkoutDisabledContinueAriaLabel} onClick={revealCheckoutValidation} /> : null}
											</div>
										</div>

										<p className="flex items-center justify-center gap-2.5 text-center text-sm font-medium text-text-primary">
											<Lock01 className="size-6 shrink-0" />
											{PLANS_PRICING_COPY.trustFooter}
										</p>
									</div>
								</form>}
						</div>
					</div>}
			</div>

			<FAQSection faqs={PLANS_FAQS} description={PLANS_FAQ_DESCRIPTION} className="bg-secondary" />
		</div>;
}